import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/context';
import { changeLanguage } from '@/i18n/config';
import { useAnnounce } from '@/features/shell/LiveRegion';
import { AppConstants } from '@/utils/constants';
import { setUserLocale, type Locale } from '@/utils/locale';
import { currentUserKey } from '@/utils/session';
import { preferencesStore, usePreferencesState } from '.';
import type { Preferences } from '@/api/preferences';

const LOCALES: Record<Preferences['language'], Locale> = {
  ja: 'ja-JP',
  en: 'en-US',
};

/** The language the user chose on this device (cookie), if any. */
export const deviceLanguage = (): Preferences['language'] | null => {
  const row = document.cookie
    .split('; ')
    .find(c => c.startsWith(`${AppConstants.locale.cookieName}=`));
  if (!row) return null;
  return row.split('=')[1] === 'en-US' ? 'en' : 'ja';
};

/**
 * Signed in: load this user's preferences (cache first, then the server),
 * keep the UI language in step with them, and report failed saves with Retry.
 */
const PreferencesSession = () => {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const state = usePreferencesState();
  const announce = useAnnounce();
  const [api, contextHolder] = notification.useNotification();
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const key = currentUserKey(user);
    if (!key || startedFor.current === key) return;
    startedFor.current = key;
    // Already started at boot for this user (bootPrefetch, D-06).
    if (preferencesStore.getState().userKey === key) return;
    void preferencesStore.start(key, deviceLanguage());
  }, [user]);

  const language = state.prefs.language;
  const active = state.userKey != null && state.source !== 'default';
  useEffect(() => {
    if (!active) return;
    const locale = LOCALES[language];
    setUserLocale(locale);
    void changeLanguage(locale);
  }, [active, language]);

  useEffect(() => {
    const key = 'preferences-save';
    if (!state.saveFailed) {
      api.destroy(key);
      return;
    }
    const message = t('v2.shell.prefsSaveFailed');
    announce(message, 'assertive');
    api.error({
      key,
      message,
      duration: 0,
      placement: 'top',
      actions: (
        <button
          type="button"
          className="rqa-toast-action"
          onClick={() => void preferencesStore.retry()}
        >
          {t('v2.common.retry')}
        </button>
      ),
    });
  }, [state.saveFailed, api, t, announce]);

  return contextHolder;
};

export default PreferencesSession;
